// Campagne-DTO's en gedeelde types.
//
// Oorspronkelijk de "Grote Vakantie Campagne" (spec
// docs/superpowers/plans/2026-05-19-grote-vakantie-campagne.md). Sinds batch-67
// (FO-501/502/503) is het model generiek: één `Campagne` met een `soort`
// (BIJTELLING / VAKANTIE / EVENEMENT) en één `CampagneDeelname` per medewerker.
// De vakantie-DTO's hieronder blijven ongewijzigd (adapter in
// `apps/api/src/routes/vakantie-campagnes.ts`); de generieke types staan in het
// tweede deel van dit bestand.

import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'

// ─── Generiek: soort, status, doelgroep ──────────────────────────────────────

// batch-70 uitrusting-controle (FO-531): UITRUSTINGSCONTROLE (kind CampagneUitrustingscontrole).
export const CAMPAGNE_SOORT = ['BIJTELLING', 'VAKANTIE', 'EVENEMENT', 'UITRUSTINGSCONTROLE'] as const
export type CampagneSoort = (typeof CAMPAGNE_SOORT)[number]

export const CAMPAGNE_SOORT_LABELS: Record<CampagneSoort, string> = {
  BIJTELLING: 'Bijtellingskeuze',
  VAKANTIE: 'Vakantieplanning',
  EVENEMENT: 'Evenement',
  UITRUSTINGSCONTROLE: 'Uitrustingscontrole',
}

export const CAMPAGNE_STATUS = ['CONCEPT', 'OPEN', 'GESLOTEN', 'AFGEROND'] as const
export type CampagneStatus = (typeof CAMPAGNE_STATUS)[number]

export const CAMPAGNE_STATUS_LABELS: Record<CampagneStatus, string> = {
  CONCEPT: 'Concept',
  OPEN: 'Open',
  GESLOTEN: 'Gesloten',
  AFGEROND: 'Afgerond',
}

export const CAMPAGNE_DOELGROEP = [
  'ALLE_MEDEWERKERS',
  'VASTE_BERIJDERS',
  'IN_DIENST_OP_EVENEMENTDATUM',
  // batch-70 uitrusting-controle (FO-531 §A1)
  'MET_UITRUSTING',
] as const
export type CampagneDoelgroep = (typeof CAMPAGNE_DOELGROEP)[number]

export const CAMPAGNE_DOELGROEP_LABELS: Record<CampagneDoelgroep, string> = {
  ALLE_MEDEWERKERS: 'Alle medewerkers in dienst',
  VASTE_BERIJDERS: 'Vaste berijders van een bedrijfsauto',
  IN_DIENST_OP_EVENEMENTDATUM: 'Iedereen in dienst op de evenementdatum',
  MET_UITRUSTING: 'Medewerkers met uitgegeven uitrusting of een open bruikleen',
}

/**
 * Statusreeks per deelname. De eerste acht zijn het bestaande vakantie-
 * vocabulaire (gemigreerd); OPGEVOLGD en VERVALLEN zijn generiek (batch-67).
 * "AFGEKEURD" in de specs is AFGEWEZEN.
 */
export const CAMPAGNE_DEELNAME_STATUS = [
  'NIET_GESTART',
  'CONCEPT',
  'INGEDIEND',
  'GEEN_AANVRAAG',
  'IN_BEOORDELING',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'WACHTLIJST',
  'OPGEVOLGD',
  'VERVALLEN',
] as const
export type CampagneDeelnameStatus = (typeof CAMPAGNE_DEELNAME_STATUS)[number]

export const CAMPAGNE_DEELNAME_STATUS_LABELS: Record<CampagneDeelnameStatus, string> = {
  NIET_GESTART: 'Niet gestart',
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend',
  GEEN_AANVRAAG: 'Geen aanvraag',
  IN_BEOORDELING: 'In beoordeling',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  WACHTLIJST: 'Wachtlijst',
  OPGEVOLGD: 'Opgevolgd',
  VERVALLEN: 'Vervallen',
}

/**
 * Statussen waarin de medewerker heeft gereageerd en dus geen herinnering of
 * "actie vereist"-kaart meer krijgt. (Vakantie: CONCEPT telt NIET als gereageerd,
 * de wizard is dan nog niet ingediend.)
 */
export const CAMPAGNE_GEREAGEERD_STATUSSEN: readonly CampagneDeelnameStatus[] = [
  'INGEDIEND',
  'GEEN_AANVRAAG',
  'IN_BEOORDELING',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'WACHTLIJST',
  'OPGEVOLGD',
  'VERVALLEN',
]

/** Momenten die de cron per campagne kan versturen (dedup-spoor). */
export const CAMPAGNE_NOTIFICATIE_TYPE = [
  'EERSTE_MELDING',
  'HERINNERING_2W',
  'HERINNERING_NIEUWJAAR',
  'WEKELIJKSE_HERINNERING',
  'DEADLINE_BEREIKT',
  'CAMPAGNE_GESLOTEN',
  'DEADLINE_2W',
  'DEADLINE_1W',
  'DEADLINE_2D',
  'DAGELIJKSE_HERINNERING',
  'HANDMATIGE_HERINNERING',
] as const
export type CampagneNotificatieType = (typeof CAMPAGNE_NOTIFICATIE_TYPE)[number]

/**
 * Optionele afwijking van het standaard-herinneringsschema per soort
 * (`Campagne.herinneringSchema`). Alle velden optioneel; `null`-campagneveld =
 * de code-default per soort (zie `apps/api/src/lib/campagne/schema.ts`).
 */
export const campagneHerinneringSchemaOverrideSchema = z
  .object({
    /** Dagen vóór de deadline waarop een eenmalige herinnering uitgaat (bv. [14, 7, 2]). */
    dagenVoorDeadline: z.array(z.number().int().min(1).max(120)).max(10).optional(),
    /** Vanaf hoeveel dagen vóór de deadline er dagelijks een herinnering uitgaat (0 = nooit). */
    dagelijksLaatsteDagen: z.number().int().min(0).max(30).optional(),
  })
  .strict()
export type CampagneHerinneringSchemaOverride = z.infer<
  typeof campagneHerinneringSchemaOverrideSchema
>

// ─── Vakantie (bestaand, ongewijzigd DTO-contract) ───────────────────────────

export const VAKANTIE_CAMPAGNE_TYPE = ['GROTE_VAKANTIE', 'BOUWVAK', 'KERSTVAKANTIE', 'SCHOOLVAKANTIE', 'OVERIG'] as const
export type VakantieCampagneType = (typeof VAKANTIE_CAMPAGNE_TYPE)[number]

export const VAKANTIE_CAMPAGNE_STATUS = CAMPAGNE_STATUS
export type VakantieCampagneStatus = CampagneStatus

export const VAKANTIE_PRIORITEIT_REGEL = ['FIRST_COME', 'VORIG_JAAR_AFGEWEZEN', 'SENIORITEIT', 'SCHOOLVAKANTIE', 'GEEN'] as const
export type VakantiePrioriteitRegel = (typeof VAKANTIE_PRIORITEIT_REGEL)[number]

/** Adapter-alias (batch-67): de vakantie-UI spreekt over "inschrijvingen". */
export const CAMPAGNE_INSCHRIJVING_STATUS = CAMPAGNE_DEELNAME_STATUS
export type CampagneInschrijvingStatus = CampagneDeelnameStatus

export const VAKANTIE_VOORKEUR_TYPE = ['HOOFDVAKANTIE', 'BOUWVAK', 'SCHOOLVAKANTIE', 'WINTERSPORT', 'LOSSE_VAKANTIE', 'ANDERS'] as const
export type VakantieVoorkeurType = (typeof VAKANTIE_VOORKEUR_TYPE)[number]

export const BEZETTING_STATUS = ['GROEN', 'ORANJE', 'ROOD'] as const
export type BezettingStatus = (typeof BEZETTING_STATUS)[number]

export interface VakantieCampagneListDTO {
  id: string
  naam: string
  jaar: number
  type: VakantieCampagneType
  status: VakantieCampagneStatus
  aanvraagDeadline: string
  startNotificatie: string
  aantalInschrijvingen: number
  aantalIngediend: number
  aantalGoedgekeurd: number
  aantalAfgewezen: number
}

export interface CampagneVoorkeurDTO {
  id: string
  volgnummer: number
  startDatum: string
  eindDatum: string
  uren: number
  type: VakantieVoorkeurType
  opmerking: string | null
  verwachtSaldoNa: number | null
  bezettingsStatus: BezettingStatus
  // ── batch-69 vakantiecampagne (FO-519): omschrijving en besluit PER periode ──
  /** Vrije omschrijving van de wens ("zomer met het gezin", "wintersport"). */
  omschrijving: string | null
  /** Besluit over deze periode; null = nog niet beoordeeld. */
  besluit: CampagneVoorkeurBesluit | null
  /** Reden bij afwijzen (of de opmerking bij goedkeuren / wachtlijst). */
  besluitReden: string | null
  beoordeeldOp: string | null
  /** De verlofaanvraag van een goedgekeurde periode. */
  leaveRequestId: string | null
  /** De verlofaanvraag staat onder voorbehoud (saldo of overlap) tot HR classificeert. */
  onderVoorbehoud: boolean
  voorbehoudReden: string | null
  /** De verlofaanvraag gaat niet meer door (afgekeurd, ingetrokken, verwijderd). */
  verlofUitkomst: CampagneVerlofUitkomst | null
  /**
   * Afgewezen en in een herkansing vervangen door een nieuwe periode: hij staat er als
   * geschiedenis bij (met reden), buiten de prioriteitsvolgorde. Niet bewerkbaar.
   */
  vervangen: boolean
  /**
   * batch-70 (FO-525 §E19): is het besluit naar de medewerker verstuurd? Tot dan is het
   * intern: het antwoord aan de medewerker toont geen besluit ("In beoordeling").
   */
  uitkomstVerstuurd?: boolean
}

export interface VerwachtSaldoEmmerDTO {
  type:
    | 'WETTELIJK'
    | 'BOVENWETTELIJK'
    | 'ATV'
    | 'TVT_WERK'
    | 'TVT_REIS'
    | 'OUDERSCHAP_BETAALD'
    | 'OUDERSCHAP_ONBETAALD'
    | 'SENIORENVERLOF'
  huidig: number
  toekomstigeOpbouw: number
  toekomstigeToekenning: number
  verval: number
  verwacht: number
}

export interface VerwachtSaldoWaarschuwingDTO {
  type: 'ONVOLDOENDE_SALDO' | 'LAAG_RESTEREND' | 'GEBASEERD_OP_VERWACHTE_OPBOUW' | 'CONTRACT_EINDIGT_VOOR_VAKANTIE' | 'VERVAL_VOOR_PEILDATUM'
  details: Record<string, unknown>
}

export interface VerwachtSaldoDTO {
  huidigBeschikbaar: number
  verwachtOpPeildatum: number
  gereserveerd: number
  vrijeRuimte: number
  perEmmer: VerwachtSaldoEmmerDTO[]
  waarschuwingen: VerwachtSaldoWaarschuwingDTO[]
}

export interface CampagneInschrijvingDTO {
  id: string
  employeeId: string
  employeeNaam: string
  employeeFunctie: string | null
  status: CampagneInschrijvingStatus
  geenVakantie: boolean
  geenVakantieOpmerking: string | null
  voorkeuren: CampagneVoorkeurDTO[]
  prioriteitsScore: number | null
  saldoSnapshot: VerwachtSaldoDTO | null
  gekozenVoorkeurId: string | null
  leaveRequestId: string | null
  beoordeeldDoor: string | null
  beoordeeldOp: string | null
  beoordelingOpmerking: string | null
  /**
   * batch-69 (FO-519): alleen in het antwoord aan de medewerker zelf. HR registreerde
   * "geen aanvraag" (met reden), niet de medewerker; de interne id van de beoordelaar
   * gaat dan niet mee.
   */
  geenAanvraagDoorHr?: boolean
  createdAt: string
  updatedAt: string
  /**
   * batch-67 (FO-502): de goedgekeurde voorkeur is als verlofaanvraag geboekt
   * maar het saldo was ontoereikend of de periode overlapt met ander verlof; de
   * aanvraag staat "onder voorbehoud" tot HR hem classificeert. `null` zolang er
   * geen verlofaanvraag is (in het antwoord op goedkeuren ook: de controle
   * mislukte en dat kon niet worden vastgelegd).
   */
  onderVoorbehoud?: boolean | null
  /** De reden van het voorbehoud (saldotekort, overlap, of "de controle is niet gelukt"). */
  voorbehoudReden?: string | null
  /**
   * batch-67 (FO-502): de verlofaanvraag van de goedgekeurde voorkeur gaat NIET meer
   * door: afgekeurd of ingetrokken in de verlofmodule, of daar verwijderd. `null` =
   * de aanvraag loopt, of er is er geen. De verlofaanvraag is de ene waarheid; de
   * campagne toont wat daar staat in plaats van "goedgekeurd" zonder meer.
   */
  verlofUitkomst?: CampagneVerlofUitkomst | null
  /** batch-67 (FO-502): herkansingstermijn na een afwijzing (ISO) of null. */
  herkansingTot?: string | null
  /** batch-67 (FO-502): reden van HR bij "geen aanvraag registreren" of een vervallen deelname. */
  vervallenReden?: string | null
}

export interface VakantieCampagneDetailDTO extends VakantieCampagneListDTO {
  doelperiodeStart: string | null
  doelperiodeEind: string | null
  maxAantalVoorkeuren: number
  prioriteitsRegel: VakantiePrioriteitRegel
  autoGoedkeurenBijSaldo: boolean
  bezettingsDrempel: number | null
  entityIds: string[]
  departmentIds: string[]
  aangemaaktDoor: string
  inschrijvingen: CampagneInschrijvingDTO[]
  /** batch-67 (FO-502): herkansingstermijn in dagen na een afwijzing. */
  herkansingDagen?: number
  /**
   * batch-69 (FO-519): opent alleen via de knop Vrijgeven (niet vanzelf op de
   * startdatum). Vrijgeven zet de startdatum op het moment van vrijgeven.
   */
  handmatigOpenen?: boolean
  /** batch-69 (FO-519 §4): wat de kijker met deze campagne mag. */
  rechten: VakantieCampagneRechtenDTO
  /** batch-70 (FO-525 §5): de instellingen van de aanvraag (HR en beheer). */
  spelregels?: string | null
  uitkomstDeadline?: string | null
  maxLosseDagen?: number
}

/**
 * batch-69 (FO-519 §4): wat de kijker met de vakantiecampagne mag. De backoffice leest dit uit
 * de API in plaats van VERLOF "volledig": een teamleider of manager heeft dat standaard en
 * beoordeelt mee, maar richt de campagne niet in en geeft hem niet vrij.
 */
export interface VakantieCampagneRechtenDTO {
  /** Aanmaken, instellingen wijzigen, vrijgeven, sluiten en afronden (HR en beheer). */
  inrichten: boolean
}

/**
 * batch-67 (FO-502): wat de MEDEWERKER zelf van een vakantiecampagne leest
 * (portal, `GET /verlof/campagnes/:id/mijn-campagne`). Bewust smal: zonder de
 * inschrijvingen, tellers en instellingen uit de backoffice-detail, want daarin
 * staan de vakantievoorkeuren van collega's.
 */
export type VakantieCampagnePortalDTO = Pick<
  VakantieCampagneDetailDTO,
  | 'id'
  | 'naam'
  | 'jaar'
  | 'type'
  | 'status'
  | 'aanvraagDeadline'
  | 'startNotificatie'
  | 'doelperiodeStart'
  | 'doelperiodeEind'
  | 'maxAantalVoorkeuren'
  // batch-69 (FO-519): de spelregels in stap 1 noemen de prioriteitsregel in gewone taal.
  | 'prioriteitsRegel'
  | 'autoGoedkeurenBijSaldo'
>

export interface DagBezettingDTO {
  datum: string
  totaalMedewerkers: number
  afwezigGoedgekeurd: number
  afwezigGereserveerd: number
  aanwezigVerwacht: number
  bezettingsgraad: number
  status: BezettingStatus
}

export interface CampagneBezettingsCheckDTO {
  campagneId: string
  teamId: string | null
  periodeStart: string
  periodeEind: string
  drempel: number
  dagBezetting: DagBezettingDTO[]
}

// ─── Generiek: "actie vereist" (portal-dashboard) ────────────────────────────

/**
 * batch-69 (FO-519 §B3): de laatste vakantiecampagne waarin de medewerker zelf meedeed en
 * waarvan de deadline voorbij is (`GET /verlof/campagnes/mijn-uitslag`), voor de ingang op
 * Mijn verlof na de deadline. `null` = er is niets te tonen.
 */
export interface CampagneMijnUitslagDTO {
  campagneId: string
  naam: string
  jaar: number
  aanvraagDeadline: string
  status: CampagneInschrijvingStatus
}

/**
 * Een openstaande campagne-actie voor de ingelogde medewerker (`GET /me/acties`).
 * Server-side afgeleid; verdwijnt zodra de deelname een gereageerd-status heeft.
 */
/**
 * batch-69 planbord-afspraak (FO-523) - de soorten op de actiekaart: de drie
 * campagnesoorten plus "gespreksverslag van je afspraak" (geen campagne).
 */
export type ActieVereistSoort = CampagneSoort | 'AFSPRAAK_VERSLAG'

export interface ActieVereistDTO {
  /** Stabiele sleutel: `CAMPAGNE:<campagneId>` of (batch-69) `AFSPRAAK:<afspraakId>`. */
  id: string
  soort: ActieVereistSoort
  /** De campagne; null bij een actie die niet uit een campagne komt (afspraakverslag). */
  campagneId: string | null
  /** Deelname-id als die al bestaat (bijtelling/evenement altijd; vakantie lazy). */
  deelnameId: string | null
  titel: string
  omschrijving: string
  /** ISO-instant van de (eigen) deadline, of null. */
  deadline: string | null
  /** Hele kalenderdagen (Europe/Amsterdam) tot de deadline; 0 = vandaag; negatief = verstreken. */
  dagenResterend: number | null
  /** Portal-route naar het formulier. */
  href: string
  /**
   * Visueel thema van de kaart: KERST (evenement), ZOMER (vakantiecampagne,
   * batch-69 FO-519), GEREEDSCHAP (uitrustingscontrole, batch-70 FO-531).
   */
  thema: 'KERST' | 'ZOMER' | 'GEREEDSCHAP' | null
  /**
   * batch-69 (FO-519 §B3): de voortgang in gewone taal ("Nog niet ingevuld",
   * "Concept opgeslagen, nog niet ingediend", "Beoordeeld: 2 goedgekeurd, 1
   * afgewezen"). Optioneel: alleen de vakantiekaart vult hem.
   */
  voortgang?: string | null
  /**
   * batch-69 (FO-519): er loopt een herkansing na een afwijzing. Bij vakantie kan
   * dat ook naast goedgekeurde periodes, dus de status zegt het niet altijd.
   */
  herkansing?: boolean
  /** Huidige deelname-status (null zonder deelname). */
  status: CampagneDeelnameStatus | null
  /** CONCEPT (vakantie): er staat al een niet-ingediende wizard klaar. */
  conceptAanwezig: boolean
}

// ─── Generiek: backoffice-overzicht over alle soorten ────────────────────────

export interface CampagneOverzichtRijDTO {
  id: string
  soort: CampagneSoort
  naam: string
  jaar: number
  status: CampagneStatus
  startDatum: string
  /** Opent niet vanzelf op `startDatum` (evenement zonder geplande vrijgave). */
  handmatigOpenen: boolean
  deadline: string
  doelgroep: CampagneDoelgroep
  aantalDoelgroep: number
  aantalIngediend: number
  aantalBeoordeeld: number
  aantalOpgevolgd: number
  aantalOpen: number
  /** Route naar het soort-specifieke beheerscherm. */
  beheerHref: string
}

/** Voortgang van een campagne voor één groep (een BV of een afdeling). */
/** Rechten van de ingelogde gebruiker op het campagne-overzicht (uit `meta.rechten`). */
export interface CampagneOverzichtRechtenDTO {
  /** Mag een bijtellingscampagne aanmaken (HR en beheer, FO-501 §4). */
  bijtellingInrichten: boolean
  /** batch-70 (FO-531 §5): mag een uitrustingscontrole aanmaken (HR/ADMIN of ASSETS volledig). */
  uitrustingInrichten?: boolean
}

export interface CampagneVoortgangRijDTO {
  /** Entity- of afdelings-id; null = medewerkers zonder BV/afdeling. */
  id: string | null
  naam: string
  /** Aantal medewerkers in de doelgroep. */
  doelgroep: number
  /** Gereageerd en wacht op beoordeling. */
  ingediend: number
  /** Beoordeeld (goedgekeurd, afgewezen, wachtlijst of opgevolgd). */
  beoordeeld: number
  /** Omgezet: vakantie = verlofaanvraag aangemaakt; bijtelling = verklaring vastgelegd. */
  omgezet: number
  /** Bewust geen aanvraag (vakantie). */
  geenAanvraag: number
  /** Doelgroep die nog niet gereageerd heeft. */
  open: number
}

/** Campagne-dashboard (FO-501 §C8, FO-502 §2.5): voortgang per BV en per afdeling. */
export interface CampagneVoortgangDTO {
  perEntiteit: CampagneVoortgangRijDTO[]
  perAfdeling: CampagneVoortgangRijDTO[]
}

export interface CampagneBezettingsconflictMedewerkerDTO {
  employeeId: string
  naam: string
  /** De gekozen periode is goedgekeurd; anders is het een nog niet beoordeelde voorkeur. */
  goedgekeurd: boolean
}

/** Te veel monteurs van één klant tegelijk met vakantie in dezelfde ISO-week (FO-502 §2.5). */
export interface CampagneBezettingsconflictDTO {
  clientId: string
  klantNaam: string
  jaar: number
  week: number
  /** bv. 2027-W31 */
  weekLabel: string
  /** Aantal monteurs van deze klant dat in die week (mogelijk) weg is. */
  aantal: number
  aantalGoedgekeurd: number
  /** Hoeveel er tegelijk weg mogen bij deze klant. */
  toegestaan: number
  /** Aantal monteurs dat bij deze klant hoort. */
  monteursBijKlant: number
  medewerkers: CampagneBezettingsconflictMedewerkerDTO[]
}

/** Achterblijver (nog niet gereageerd) in een campagne. */
export interface CampagneAchterblijverDTO {
  deelnameId: string | null
  employeeId: string
  employeeNaam: string
  entityNaam: string | null
  departmentNaam: string | null
  status: CampagneDeelnameStatus | null
  deadline: string
  laatsteHerinnering: string | null
  /**
   * Kan een herinnering deze medewerker bereiken? `false` = geen portaltoegang of
   * nog niet gestart: hij staat in de lijst, maar HR benadert hem persoonlijk.
   */
  bereikbaar: boolean
}

export const campagneHerinnerNuSchema = z
  .object({
    /** Alleen deze medewerkers; leeg = alle achterblijvers. */
    employeeIds: z.array(z.string().min(1)).max(500).optional(),
  })
  .strict()
export type CampagneHerinnerNuInput = z.infer<typeof campagneHerinnerNuSchema>

export const campagneGeenAanvraagSchema = z
  .object({
    employeeId: z.string().min(1),
    reden: z.string().trim().min(3, 'Geef een reden op').max(500),
  })
  .strict()
export type CampagneGeenAanvraagInput = z.infer<typeof campagneGeenAanvraagSchema>

/**
 * Statussen waarin een verlofaanvraag nog niet door HR is geclassificeerd. Een
 * voorbehoud (saldotekort of overlap bij een goedgekeurde vakantievoorkeur) staat
 * zolang open: "beoordeel dit bij de classificatie". Daarna is het afgehandeld en
 * hoort het label van de schermen te verdwijnen.
 */
const VERLOF_VOORBEHOUD_OPEN_STATUSSEN: ReadonlySet<string> = new Set([
  'CONCEPT',
  'INGEDIEND',
  'AANVULLENDE_VRAAG',
  'GOEDGEKEURD_TEAMLEIDER',
  'OVERRULED',
])

/** Waarom de verlofaanvraag van een goedgekeurde voorkeur niet meer doorgaat. */
export type CampagneVerlofUitkomst = 'AFGEKEURD' | 'INGETROKKEN' | 'VERWIJDERD'

export const CAMPAGNE_VERLOF_UITKOMST_LABELS: Record<CampagneVerlofUitkomst, string> = {
  AFGEKEURD: 'verlofaanvraag afgekeurd',
  INGETROKKEN: 'verlofaanvraag ingetrokken',
  VERWIJDERD: 'verlofaanvraag verwijderd',
}

const VERLOF_AFGEKEURD_STATUSSEN: ReadonlySet<string> = new Set(['AFGEKEURD', 'AFGEKEURD_TEAMLEIDER', 'AFGEWEZEN'])

/**
 * Gaat de verlofaanvraag van een goedgekeurde vakantievoorkeur nog door? `null`
 * = ja (of er is geen aanvraag). Een voorbehoud eindigt doordat HR classificeert,
 * of doordat de aanvraag wordt afgekeurd of ingetrokken. De verlofmodule weet
 * niets van de campagne, dus de campagne leest het af aan de aanvraag: de
 * beoordeling, het bezettingsoverzicht, de voortgang en de uitslag voor de
 * medewerker gebruiken deze ene regel.
 */
export function bepaalCampagneVerlofUitkomst(
  r: { status: string; deletedAt?: Date | string | null } | null | undefined,
): CampagneVerlofUitkomst | null {
  if (!r) return null
  if (r.deletedAt != null) return 'VERWIJDERD'
  if (r.status === 'INGETROKKEN') return 'INGETROKKEN'
  if (VERLOF_AFGEKEURD_STATUSSEN.has(r.status)) return 'AFGEKEURD'
  return null
}

/**
 * Staat het voorbehoud op deze verlofaanvraag nog open? De ene regel voor de
 * campagne-beoordeling, de verlofmodule en de portal: de vlag op de aanvraag EN
 * een status waarin HR nog moet classificeren.
 */
export function isVerlofVoorbehoudOpen(r: { onderVoorbehoud?: boolean | null; status: string }): boolean {
  return r.onderVoorbehoud === true && VERLOF_VOORBEHOUD_OPEN_STATUSSEN.has(r.status)
}

// ─── batch-69 vakantiecampagne (FO-519): wensen per periode ──────────────────
//
// Een medewerker geeft tot `maxAantalVoorkeuren` losse periodes op (zomer,
// wintersport, een bruiloft), elk met een omschrijving. De planning beslist PER
// periode: goedkeuren (een eigen verlofaanvraag), afwijzen (met herkansing) of
// wachtlijst. De deelname-status volgt uit de besluiten
// (`bepaalDeelnameStatusUitBesluiten` in @streep/utils).

// batch-70 vakantiecampagne (FO-525 §D14): IN_OVERLEG (te veel aanvragen tegelijk, we
// gaan in overleg), VERVANGEN (na indienen gewijzigd: een nieuwe wens neemt het over) en
// INGETROKKEN (door de medewerker). Losse ADD VALUE-migratie b70_vakantiecampagne_enum_waarden.
export const CAMPAGNE_VOORKEUR_BESLUIT = ['GOEDGEKEURD', 'AFGEWEZEN', 'WACHTLIJST', 'IN_OVERLEG', 'VERVANGEN', 'INGETROKKEN'] as const
export type CampagneVoorkeurBesluit = (typeof CAMPAGNE_VOORKEUR_BESLUIT)[number]

export const CAMPAGNE_VOORKEUR_BESLUIT_LABELS: Record<CampagneVoorkeurBesluit, string> = {
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  WACHTLIJST: 'Wachtlijst',
  IN_OVERLEG: 'In overleg',
  VERVANGEN: 'Vervangen',
  INGETROKKEN: 'Ingetrokken',
}

/** Statusfilter van het periode-overzicht; OPEN = nog niet beoordeeld. */
export const CAMPAGNE_PERIODE_STATUS_FILTER = ['OPEN', 'GOEDGEKEURD', 'AFGEWEZEN', 'WACHTLIJST'] as const
export type CampagnePeriodeStatusFilter = (typeof CAMPAGNE_PERIODE_STATUS_FILTER)[number]

export const CAMPAGNE_PERIODE_STATUS_FILTER_LABELS: Record<CampagnePeriodeStatusFilter, string> = {
  OPEN: 'Nog te beoordelen',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  WACHTLIJST: 'Wachtlijst',
}

/** Keuzelijst bij het afwijzen van een periode (spec 219 §7). */
export const CAMPAGNE_AFWIJS_REDEN = ['BEZETTING_KLANT', 'SALDO', 'ANDER_VERLOF', 'OVERIG'] as const
export type CampagneAfwijsReden = (typeof CAMPAGNE_AFWIJS_REDEN)[number]

export const CAMPAGNE_AFWIJS_REDEN_LABELS: Record<CampagneAfwijsReden, string> = {
  BEZETTING_KLANT: 'Bezetting bij de klant',
  SALDO: 'Onvoldoende saldo',
  ANDER_VERLOF: 'Ander verlof al goedgekeurd',
  OVERIG: 'Overig',
}

/**
 * De reden zoals hij wordt opgeslagen en aan de medewerker gemeld: het label van
 * de keuze, met de toelichting erachter ("Bezetting bij de klant: in week 31 zijn
 * er al vier weg").
 */
export function afwijsRedenTekst(soort: CampagneAfwijsReden, toelichting?: string | null): string {
  const label = CAMPAGNE_AFWIJS_REDEN_LABELS[soort]
  const t = toelichting?.trim()
  return t ? `${label}: ${t}` : label
}

/** Afwijzen van een periode: reden verplicht (keuzelijst), toelichting verplicht bij Overig. */
export const campagnePeriodeAfwijzenSchema = z
  .object({
    redenSoort: z.enum(CAMPAGNE_AFWIJS_REDEN, { error: 'Kies een reden' }),
    toelichting: z.string().trim().max(900, 'De toelichting is te lang (maximaal 900 tekens)').optional(),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.redenSoort === 'OVERIG' && (d.toelichting ?? '').length < 3) {
      ctx.addIssue({ code: 'custom', path: ['toelichting'], message: 'Licht de reden toe (minimaal 3 tekens)' })
    }
  })
export type CampagnePeriodeAfwijzenInput = z.infer<typeof campagnePeriodeAfwijzenSchema>

/** Goedkeuren of op de wachtlijst zetten van een periode: een optionele opmerking. */
export const campagnePeriodeOpmerkingSchema = z
  .object({
    opmerking: z.string().trim().max(1000, 'De opmerking is te lang (maximaal 1000 tekens)').optional(),
  })
  .strict()
export type CampagnePeriodeOpmerkingInput = z.infer<typeof campagnePeriodeOpmerkingSchema>

/** Maximaal aantal periodes in een keer goedkeuren (de beoordeling van een campagne). */
export const CAMPAGNE_BULK_GOEDKEUREN_MAX = 200

export const campagnePeriodesBulkGoedkeurenSchema = z
  .object({
    voorkeurIds: z
      .array(idParam('voorkeurId'))
      .min(1, 'Selecteer minstens een periode')
      .max(CAMPAGNE_BULK_GOEDKEUREN_MAX, `Keur maximaal ${CAMPAGNE_BULK_GOEDKEUREN_MAX} periodes tegelijk goed`)
      .refine((ids) => new Set(ids).size === ids.length, { message: 'Een periode staat dubbel in de selectie' }),
    opmerking: z.string().trim().max(1000, 'De opmerking is te lang (maximaal 1000 tekens)').optional(),
  })
  .strict()
export type CampagnePeriodesBulkGoedkeurenInput = z.infer<typeof campagnePeriodesBulkGoedkeurenSchema>

/**
 * Filters van het periode-overzicht, gedeeld door de lijstroute en de export
 * (zodat de export precies toont wat het scherm toont).
 */
export const campagnePeriodesFilterSchema = z.object({
  status: z.enum(CAMPAGNE_PERIODE_STATUS_FILTER).optional(),
  departmentId: idParam('departmentId').optional(),
  clientId: idParam('clientId').optional(),
  /** ISO-week als `2027-W31`: de periodes die die week raken. */
  week: z.string().regex(/^\d{4}-W\d{2}$/, 'Geef de week als jjjj-Www (bijvoorbeeld 2027-W31)').optional(),
  /** Alleen periodes in een week met een bezettingsconflict bij een van hun klanten. */
  alleenConflicten: queryBoolean().optional(),
})
export type CampagnePeriodesFilter = z.infer<typeof campagnePeriodesFilterSchema>

/** Een conflict (te veel monteurs van een klant tegelijk weg) dat deze periode raakt. */
export interface CampagnePeriodeConflictDTO {
  clientId: string
  klantNaam: string
  jaar: number
  week: number
  /** Aantal monteurs van deze klant dat die week (mogelijk) weg is. */
  aantal: number
  /** Hoeveel er tegelijk weg mogen. */
  toegestaan: number
}

/** Eén periode van een vakantiewens als regel in de beoordeling (en de export). */
export interface CampagnePeriodeRijDTO {
  voorkeurId: string
  deelnameId: string
  employeeId: string
  employeeNaam: string
  employeeNummer: string | null
  afdelingNaam: string | null
  entityNaam: string | null
  /** De klant(en) waar de monteur werkt (recent of gepland in de periode). */
  klantNamen: string[]
  /** Prioriteit van de medewerker zelf: 1 = belangrijkst. */
  volgnummer: number
  startDatum: string
  eindDatum: string
  /** "wk 30-31" */
  weekLabel: string
  uren: number
  omschrijving: string | null
  opmerking: string | null
  /** Verwacht saldo na deze periode, zoals berekend bij het indienen. */
  verwachtSaldoNa: number | null
  besluit: CampagneVoorkeurBesluit | null
  besluitReden: string | null
  beoordeeldOp: string | null
  beoordeeldDoorNaam: string | null
  leaveRequestId: string | null
  onderVoorbehoud: boolean
  voorbehoudReden: string | null
  verlofUitkomst: CampagneVerlofUitkomst | null
  deelnameStatus: CampagneDeelnameStatus
  /** Prioriteitsscore van de deelname volgens de campagneregel (hoger = eerder). */
  prioriteitsScore: number | null
  conflicten: CampagnePeriodeConflictDTO[]
  /** Er kan (nog) over deze periode worden beslist (open of wachtlijst). */
  beoordeelbaar: boolean
  /**
   * Telt deze periode nu mee in de bezetting: nog niet beoordeeld in een ingediende
   * deelname, of goedgekeurd en de verlofaanvraag loopt. Een afgewezen periode of een
   * periode op de wachtlijst telt niet mee.
   */
  teltMeeInBezetting: boolean
  /** Afgewezen en in een herkansing vervangen: geschiedenis, geen prioriteit meer. */
  vervangen: boolean
  /**
   * Over deze periode mag de kijker zelf niet beslissen: het is zijn eigen wens (4-ogen,
   * regel 18). Hij staat er wel, maar niet selecteerbaar.
   */
  eigenWens: boolean
  /** De periode loopt door na de (geplande) einddatum van het dienstverband: goedkeuren kan niet. */
  naEinddatum: boolean
}

/** De tegels boven de beoordeling: over de hele campagne, los van de filters. */
export interface CampagnePeriodeTellersDTO {
  doelgroep: number
  /** Medewerkers die hun wensen indienden (ook als ze al beoordeeld zijn). */
  ingediend: number
  /** Medewerkers waarvan alle periodes beoordeeld zijn. */
  beoordeeld: number
  geenWensen: number
  nogNiet: number
  periodesOpen: number
  periodesGoedgekeurd: number
  periodesAfgewezen: number
  periodesWachtlijst: number
  /** Aantal (klant, week)-combinaties met te veel monteurs tegelijk weg. */
  wekenBovenDrempel: number
}

export interface CampagneGeenWensenDTO {
  deelnameId: string
  employeeId: string
  employeeNaam: string
  afdelingNaam: string | null
  opmerking: string | null
  /** HR registreerde "geen aanvraag" (met reden), niet de medewerker zelf. */
  doorHr: boolean
}

export interface CampagnePeriodeOverzichtDTO {
  rijen: CampagnePeriodeRijDTO[]
  tellers: CampagnePeriodeTellersDTO
  geenWensen: CampagneGeenWensenDTO[]
  filterOpties: {
    klanten: Array<{ id: string; naam: string }>
    weken: Array<{ waarde: string; label: string }>
  }
  /**
   * De bezettingsberekening lukte niet: de conflicten staan dan leeg. Het scherm
   * zegt dat, want "geen conflict" zou iets beloven wat niet is gecontroleerd.
   */
  bezettingOnbekend: boolean
}

/** Uitkomst van een besluit over één periode (ook per regel in een bulkactie). */
export interface CampagnePeriodeBesluitUitkomstDTO {
  voorkeurId: string
  ok: boolean
  /** Bij `ok: false`: de foutcode en de reden. */
  code?: string
  melding?: string
  /** Na goedkeuren: de aanvraag staat onder voorbehoud; null = de controle is niet gelukt. */
  onderVoorbehoud?: boolean | null
  voorbehoudReden?: string | null
}

export interface CampagneBulkGoedkeurenDTO {
  resultaten: CampagnePeriodeBesluitUitkomstDTO[]
  goedgekeurd: number
  mislukt: number
}

// ── Voorvertoning in de wizard (saldo-effect, overlap, bezettingshint) ───────

export const campagneVoorvertoningSchema = z
  .object({
    voorkeuren: z
      .array(
        z.object({
          volgnummer: z.number().int().min(1).max(5),
          startDatum: z.string().datetime(),
          eindDatum: z.string().datetime(),
        }),
      )
      .min(1)
      .max(5),
  })
  .strict()
export type CampagneVoorvertoningInput = z.infer<typeof campagneVoorvertoningSchema>

export interface CampagneVoorvertoningPeriodeDTO {
  volgnummer: number
  werkdagen: number
  uren: number
  /** Verwacht saldo na deze periode, als ook de belangrijkere periodes ervoor doorgaan. */
  saldoNa: number
  /** GROEN = toereikend, ORANJE = tekort maar indienen mag, ROOD = tekort en de campagne weigert. */
  toon: 'GROEN' | 'ORANJE' | 'ROOD'
  /** Eigen verlofaanvragen die deze periode al raken (lopend of geboekt). */
  overlappingen: Array<{ van: string; tot: string; statusLabel: string }>
  /** Informatief: collega's bij dezelfde klant die in die week al vakantie vroegen. */
  bezetting: Array<{ klantNaam: string; jaar: number; week: number; collegas: number; toegestaan: number }>
}

export interface CampagneVoorvertoningDTO {
  periodes: CampagneVoorvertoningPeriodeDTO[]
  totaalUren: number
  /** Verwacht saldo na alle periodes. */
  saldoNa: number
  /** Er is een tekort, maar indienen mag: de wens wordt onder voorbehoud beoordeeld. */
  onderVoorbehoud: boolean
  /** Deze campagne weigert indienen met een tekort (`autoGoedkeurenBijSaldo`). */
  geblokkeerd: boolean
  /** De bezettingscheck lukte niet; de wizard zegt dat in plaats van "geen drukte". */
  bezettingOnbekend: boolean
}

// ── Prioriteitsregel in gewone taal ──────────────────────────────────────────

export const VAKANTIE_PRIORITEIT_REGEL_LABELS: Record<VakantiePrioriteitRegel, string> = {
  FIRST_COME: 'Wie eerst indient (first come, first served)',
  VORIG_JAAR_AFGEWEZEN: 'Voorrang na een afwijzing vorig jaar',
  SENIORITEIT: 'Senioriteit (langst in dienst eerst)',
  SCHOOLVAKANTIE: 'Schoolgaande kinderen voorrang',
  GEEN: 'Geen vaste regel',
}

/**
 * De regel zoals de medewerker hem leest in stap 1 van de wizard. `null` = er is
 * niets toe te voegen aan "de planning beslist op prioriteit". SCHOOLVAKANTIE
 * leunt op het voorkeurstype, dat de wizard sinds FO-519 niet meer vraagt: daar
 * belooft de wizard dus niets.
 */
export const VAKANTIE_PRIORITEIT_UITLEG: Record<VakantiePrioriteitRegel, string | null> = {
  FIRST_COME: 'Bij gelijke prioriteit gaat wie eerder indient voor.',
  VORIG_JAAR_AFGEWEZEN: 'Wie vorig jaar een afwijzing kreeg, gaat bij gelijke prioriteit voor.',
  SENIORITEIT: 'Bij gelijke prioriteit gaat wie langer in dienst is voor.',
  SCHOOLVAKANTIE: null,
  GEEN: null,
}
